import { jsx as _jsx } from "react/jsx-runtime";
import { selectAuth, selectCandidate, selectQueryParams, selectUser } from '@containers/Auth/selectors';
import { getEmailOtp, getPhoneFirebaseOtp, initialize, storeIdToken, unAuthenticate, verifyEmailOtp, verifyFirebaseOtp, } from '@containers/Auth/slice';
import { initSocialLogin } from '@containers/Auth/helpers';
import { useFeatureValue, useGrowthBook } from '@growthbook/growthbook-react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { growthBookFeatureFlags } from '@utils/featureFlags';
import { candidateAuthenticated, setMixpanelInitialized } from '@utils/mixpanel/mixpanelActions';
import { getRuntimeConfigSync } from '@utils/runtimeConfig';
import { catchError, sentrySetUser } from '@utils/sentry';
import Mixpanel from 'mixpanel-browser';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { selectTestInstanceId } from '@containers/Assessment/selectors';
// import initChatbase from '@utils/chatbase';
import initHelpCenter from '@utils/helpCenter';
export const authContextInitialState = {
    isAuthenticated: false,
    isAuthenticating: true,
    triggerEmailOtp: (_userName, _callback) => {
        // No-op implementation for initial state
    },
    triggerPhoneFirebaseOtp: (_phoneNumber, _callback) => {
        // No-op implementation for initial state
    },
    initiateSocialLogin: (_name) => {
        // No-op implementation for initial state
    },
    triggerVerifyEmailOtp: (_code, _callback) => {
        // No-op implementation for initial state
    },
    triggerFirebaseVerifyOtp: (_code, _callback) => {
        // No-op implementation for initial state
    },
    triggerUnAuthenticate: (_shouldReload) => {
        // No-op implementation for initial state
    },
    phone: '',
    username: '',
    verificationCodeRequested: false,
    isInitialized: false,
    tokens: { accessToken: '' },
    candidate: null,
};
const AuthContext = React.createContext(authContextInitialState);
function AuthProvider(props) {
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectAuth);
    const user = useAppSelector(selectUser);
    const queryParams = useAppSelector(selectQueryParams);
    const growthBook = useGrowthBook();
    const enableMixPanel = useFeatureValue(growthBookFeatureFlags.MIX_PANEL_ENABLED, false);
    // const chatBotEnabled = useFeatureValue(growthBookFeatureFlags.CX_CHAT_BOT_ENABLED, null);
    const helpCenterEnabled = useFeatureValue(growthBookFeatureFlags.CX_HELP_CENTER_ENABLED, false);
    const [search, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const candidate = useAppSelector(selectCandidate);
    const testInstanceId = useAppSelector(selectTestInstanceId);
    const tokens = useMemo(() => {
        const { accessToken, customToken, idToken, hash_token } = state;
        return { accessToken, customToken, idToken, hash_token };
    }, [state]);
    const triggerEmailOtp = useCallback((username, callback) => {
        dispatch(getEmailOtp({ username, callback }));
    }, [dispatch]);
    const triggerPhoneFirebaseOtp = useCallback((phone, callback) => {
        dispatch(getPhoneFirebaseOtp({ phone, callback }));
    }, [dispatch]);
    const triggerVerifyEmailOtp = useCallback((code, callback) => {
        dispatch(verifyEmailOtp({
            username: user?.phone || user?.username,
            code,
            callback,
        }));
    }, [user, dispatch]);
    const triggerFirebaseVerifyOtp = useCallback((code, callback) => {
        dispatch(verifyFirebaseOtp({
            code,
            callback,
        }));
    }, [dispatch]);
    const initiateSocialLogin = (name, callback) => {
        // Open the popup synchronously inside the click's user-gesture window. Do NOT route this through a
        // saga/dispatch first: the async hop drops the browser's user-activation signal and the popup gets
        // blocked (the SCH-3597 bug). initSocialLogin is async, but signInWithPopup fires before its first
        // await, so the popup opens within this synchronous call. The resulting idToken then flows through
        // the normal storeIdToken -> signupWorker -> authenticate pipeline. See ADR 0032.
        void initSocialLogin(name)
            .then((result) => {
            if (result?.idToken) {
                dispatch(storeIdToken(result.idToken));
                callback?.onSuccess?.();
            }
            else {
                throw new Error('Id token not found');
            }
        })
            .catch((error) => {
            callback?.onError?.(error);
            catchError({ title: 'firebase social login', error });
        });
    };
    const redirectPostAuthentication = () => {
        if (queryParams?.from) {
            const path = `${queryParams?.from}`;
            navigate(path, { replace: true });
        }
        else if (queryParams?.redirect_url) {
            navigate('/redirect');
        }
    };
    const triggerUnAuthenticate = (shouldReload) => {
        dispatch(unAuthenticate({ shouldReload }));
    };
    const value = useMemo(() => ({
        user,
        queryParams,
        isAuthenticated: state.authenticated,
        isAuthenticating: state.authenticating,
        isInitialized: state.initialized,
        triggerEmailOtp,
        triggerPhoneFirebaseOtp,
        initiateSocialLogin,
        triggerVerifyEmailOtp,
        triggerFirebaseVerifyOtp,
        username: user?.username,
        phone: user?.phone,
        verificationCodeRequested: state?.verificationCodeRequested,
        tokens,
        redirectPostAuthentication,
        triggerUnAuthenticate,
        candidate,
    }), [
        user,
        queryParams,
        state.authenticated,
        state.authenticating,
        state.initialized,
        state?.verificationCodeRequested,
        triggerEmailOtp,
        triggerPhoneFirebaseOtp,
        triggerVerifyEmailOtp,
        triggerFirebaseVerifyOtp,
        tokens,
        triggerUnAuthenticate,
        candidate,
    ]);
    useEffect(() => {
        dispatch(initialize());
    }, []);
    useEffect(() => {
        if (helpCenterEnabled && value.isAuthenticated && user) {
            // Initialize Help Center with candidate credentials
            const mixpanelToken = (() => {
                try {
                    return getRuntimeConfigSync().MIXPANEL_TOKEN;
                }
                catch {
                    return undefined;
                }
            })();
            const sessionIdentifier = (testInstanceId != null ? String(testInstanceId) : undefined) ??
                queryParams?.test_instance_id ??
                queryParams?.meeting_external_id ??
                null;
            initHelpCenter({
                candidate: {
                    token: tokens.accessToken,
                    scope: `helpcenter_message_${user.user_id}`,
                },
                telemetryToken: mixpanelToken ?? null,
                telemetryHostApp: 'cx',
                sessionIdentifier,
            });
        }
    }, [helpCenterEnabled, value.isAuthenticated, user, tokens.accessToken, testInstanceId, queryParams]);
    useEffect(() => {
        const tid = search.get('tid') || user?.tenant_id || queryParams?.tid;
        growthBook?.setAttributes({
            ...growthBook.getAttributes(),
            tenant_id: tid ? Number(tid) : null,
            user_id: user?.user_id ? Number(user?.user_id) : null,
        });
        sentrySetUser(user);
        if (value.isAuthenticated) {
            candidateAuthenticated();
            // removing c token and tid from url
            if (search.get('c') || search.get('tid')) {
                search.delete('c');
                search.delete('tid');
                setSearchParams(search);
            }
        }
    }, [value.isAuthenticated, value.isAuthenticating]);
    useEffect(() => {
        const enabled = enableMixPanel && process.env.NODE_ENV === 'production';
        // Get Mixpanel token from runtime config
        let mixpanelToken;
        try {
            const config = getRuntimeConfigSync();
            mixpanelToken = config.MIXPANEL_TOKEN;
        }
        catch {
            console.warn('[AuthContext] Runtime config not available for Mixpanel initialization');
        }
        if (mixpanelToken) {
            setMixpanelInitialized(true);
            Mixpanel.init(mixpanelToken, {
                // EU data-residency host; US forwarding (api.mixpanel.com) is being retired.
                api_host: 'https://api-eu.mixpanel.com',
                ignore_dnt: true,
                persistence: 'localStorage',
                debug: false,
                opt_out_tracking_by_default: !enabled,
                track_pageview: false,
                loaded(mixpanel) {
                    growthBook.setAttributes({
                        ...growthBook.getAttributes(),
                        id: mixpanel.get_distinct_id(),
                    });
                },
            });
            if (enabled && Mixpanel && typeof Mixpanel.opt_in_tracking === 'function') {
                Mixpanel?.opt_in_tracking();
            }
            else if (typeof Mixpanel.opt_out_tracking === 'function') {
                Mixpanel?.opt_out_tracking();
            }
        }
    }, [enableMixPanel]);
    return _jsx(AuthContext.Provider, { value: value, children: props.children });
}
export const AuthConsumer = AuthContext.Consumer;
export { AuthProvider };
export default AuthContext;
